<script setup>
import { ref, computed, watch, onMounted } from 'vue'
import { useStore } from 'vuex'
import { useRouter } from 'vue-router'
import { useNotification } from '@kyvg/vue3-notification'
import { showModal } from '@/Utils/modalBus'
import ConfirmModal from '@/components/modals/ConfirmModal.vue'
import FormButton from '@/components/Atoms/FormButton.vue'
import FormInput from '@/components/Atoms/FormInput.vue'
import Alert from '@/components/Atoms/Alert.vue'
import ButtonCopyToClipboard from '@/components/Moleculas/ButtonCopyToClipboard.vue'
import IconBase from '@/components/icon/IconBase.vue'
import IconGlobe from '@/components/icon/IconGlobe.vue'
import SiteAdminService from '@/services/SiteAdminService'
import SiteStripeStatus from '@/components/Sites/SiteStripeStatus.vue'
import SiteTemplateThumb from '@/components/Sites/SiteTemplateThumb.vue'
import ActionModal from '@/components/modals/ActionModal.vue'
import StatusPill from '@/components/Atoms/StatusPill.vue'
import SiteImageField from '@/components/Sites/SiteImageField.vue'
import SitesPlanLockBadge from '@/components/Sites/SitesPlanLockBadge.vue'
import { useStripeConnect } from '@/composables/useStripeConnect'
import { useSiteHostReadiness } from '@/composables/useSiteHostReadiness'
import { useSiteDraft } from '@/composables/useSiteDraft'
import { SITE_TEMPLATES, TEMPLATE_BROADCAST, TEMPLATE_CATALOG, siteTemplate, templateMeta, SWITCH_COPY } from '@/components/Sites/siteTemplates'
import { isFeatureDisabled } from '@/services/SiteAdminService'
import { trackSites, SITES_EVENTS } from '@/lib/tracking/sites'

// Settings › General: the site's name, Castr address, home-page template,
// social share image, Stripe account and the danger zone. Status, publish
// state and revenue live on the Overview.
const props = defineProps({
	siteId: {
		type: String,
		required: true,
	},
	site: {
		type: Object,
		required: true,
	},
})

const store = useStore()
const customDomainLocked = computed(() => store.getters['Sites/customDomainLocked'])
const router = useRouter()
const { notify } = useNotification()

const site = computed(() => store.getters['Sites/siteById'](props.siteId) || props.site)
// Address not routed by the edge yet: the link is nothing to share for now.
const readiness = useSiteHostReadiness(() => props.siteId)
const addressPending = computed(() => readiness.gated.value)

// Editable copy of the name; the store holds the server truth.
const nameInput = ref('')
const savingGeneral = ref(false)
const nameDirty = computed(() => nameInput.value.trim() !== (site.value?.name || ''))

// Undo "Dismiss permanently" on the Overview setup checklist (castr site flag).
const checklistDismissed = computed(() => site.value?.flags?.setupChecklistDismissed === true)
const restoringChecklist = ref(false)
const showSetupGuide = async () => {
	if (restoringChecklist.value) return
	restoringChecklist.value = true
	try {
		await store.dispatch('Sites/updateSite', { id: props.siteId, payload: { flags: { setupChecklistDismissed: false } } })
		notify({ group: 'success', text: 'The setup guide is back on the Overview tab.' })
	} catch (err) {
		notify({ group: 'error', text: err.message || 'Could not restore the setup guide.' })
	}
	restoringChecklist.value = false
}

const syncFromSite = () => {
	if (!site.value) return
	nameInput.value = site.value.name || ''
}
watch(site, (value, prev) => {
	if (value && (!prev || value.id !== prev.id)) syncFromSite()
})

// The site name has one source: castr `PUT /sites/{id} { name }`. castr
// republishes the site record (and the runtime's `websiteName`) to KV, so
// nothing is written to the runtime draft here and no Publish is needed —
// KV propagation takes up to a minute.
const saveGeneral = async () => {
	const name = nameInput.value.trim()
	if (!name || !nameDirty.value) return
	savingGeneral.value = true
	try {
		await store.dispatch('Sites/updateSite', { id: props.siteId, payload: { name } })
		notify({ group: 'success', text: 'Site name updated. Live on the site in up to a minute.' })
	} catch (err) {
		notify({ group: 'error', text: err.message || 'Could not update the site.' })
	}
	savingGeneral.value = false
}

// Home-page template (sites-broadcast-template-contract.md §7): castr owns
// `site.template`; `PUT /sites/{id} { template }` republishes the record at
// once, so the switch is confirmed first and needs no Publish afterwards.
// Always shown — templates have no feature flag.
const template = computed(() => siteTemplate(site.value))
const otherTemplate = computed(() => templateMeta(template.value === TEMPLATE_BROADCAST ? TEMPLATE_CATALOG : TEMPLATE_BROADCAST))
const switchCopy = computed(() => SWITCH_COPY[otherTemplate.value.id])
const switchModalVisible = ref(false)
const switchingTemplate = ref(false)
const requestSwitch = () => { switchModalVisible.value = true }
const confirmSwitch = async () => {
	if (switchingTemplate.value) return
	switchingTemplate.value = true
	const target = otherTemplate.value
	const from = template.value
	try {
		const updated = await store.dispatch('Sites/updateSite', { id: props.siteId, payload: { template: target.id } })
		trackSites(SITES_EVENTS.TEMPLATE_SWITCHED, updated || props.siteId, { template: target.id, from, to: target.id })
		notify({ group: 'success', text: `Switched to the ${target.name} template. Live on the site in up to a minute.` })
		if (target.id === TEMPLATE_BROADCAST) router.push({ name: 'SiteBroadcast', params: { siteId: props.siteId } })
	} catch (err) {
		notify({ group: 'error', text: isFeatureDisabled(err) ? 'This Castr server does not offer templates yet — ask support to update it.' : (err.message || 'Could not switch the template.') })
	}
	switchingTemplate.value = false
}

// Social share image: the runtime setting `websiteImage` (og:image of every
// page except item pages), saved to the runtime draft like the Theme tab.
// Empty → the runtime renders a 1200 × 630 card from the logo / colours at
// `/api/og/home`; the preview loads the published site's card.
const draft = useSiteDraft(() => props.siteId)
const shareImage = ref('')
const shareImageSaved = ref('')
const shareLoaded = ref(false)
const shareWritable = ref(true)
const shareError = ref(null)
const savingShare = ref(false)
const autoPreviewBroken = ref(false)
const shareDirty = computed(() => shareImage.value.trim() !== shareImageSaved.value)
const shareSaveLabel = computed(() => (draft.draftSupported.value ? 'Save share image to draft' : 'Save share image'))
const siteOrigin = computed(() => {
	try { return site.value?.url ? new URL(site.value.url).origin : '' } catch { return '' }
})
const autoPreviewSrc = computed(() => {
	if (!siteOrigin.value || addressPending.value) return ''
	const v = `${site.value?.publish?.at || site.value?.lastUpdated || ''}-${draft.state.value.version}`
	return `${siteOrigin.value}/api/og/home?v=${encodeURIComponent(v)}`
})
watch(autoPreviewSrc, () => { autoPreviewBroken.value = false })

const loadShareImage = async () => {
	try {
		const res = await SiteAdminService.getSettings(props.siteId)
		const merged = { ...(res?.settings || {}), ...(res?.draft && typeof res.draft === 'object' ? res.draft : {}) }
		shareImageSaved.value = typeof merged.websiteImage === 'string' ? merged.websiteImage.trim() : ''
		shareImage.value = shareImageSaved.value
		shareWritable.value = !Array.isArray(res?.writableKeys) || res.writableKeys.includes('websiteImage')
		draft.applySettingsResponse(res)
		shareError.value = null
	} catch (err) {
		shareError.value = err.message || 'Could not load the share image.'
	}
	shareLoaded.value = true
}
const saveShareImage = async () => {
	if (!shareDirty.value || savingShare.value) return
	const websiteImage = shareImage.value.trim()
	savingShare.value = true
	try {
		const res = await SiteAdminService.updateSettings(props.siteId, { websiteImage }, { target: 'draft' })
		shareImageSaved.value = websiteImage
		draft.noteDraftSaved(res)
		notify({
			group: 'success',
			text: draft.draftSupported.value
				? `Share image ${websiteImage ? 'saved' : 'removed'} in the draft. Publish to use it on your site.`
				: `Share image ${websiteImage ? 'saved' : 'removed'}.`,
		})
	} catch (err) {
		notify({ group: 'error', text: err.message || 'Could not save the share image.' })
	}
	savingShare.value = false
}

// Stripe Connect state of the owner's paywall account (castr /monetization).
const monetization = ref(null)
const monetizationError = ref(null)
const loadMonetization = async () => {
	try {
		monetization.value = await SiteAdminService.getMonetization(props.siteId)
		monetizationError.value = null
	} catch (err) {
		monetizationError.value = err.message || 'Could not load the Stripe status.'
	}
}
const stripeConnect = useStripeConnect({ onUnlinked: loadMonetization })
const requestUnlink = () => showModal('unlink-stripe-general')
const confirmUnlink = async () => {
	try {
		const res = await stripeConnect.unlink()
		const n = res?.sites?.products ?? 0
		notify({ group: 'success', text: n ? `Stripe unlinked. ${n} paid ${n === 1 ? 'plan' : 'plans'} paused across your sites.` : 'Stripe unlinked.' })
		await store.dispatch('Sites/getSite', props.siteId).catch(() => {})
	} catch (err) {
		notify({ group: 'error', text: err.message || 'Could not unlink Stripe.' })
	}
}

onMounted(() => {
	syncFromSite()
	loadMonetization()
	loadShareImage()
})

const onDeleteRequest = () => showModal('delete-site-settings')
const onDelete = async () => {
	const name = site.value?.name
	const deleted = site.value
	try {
		await store.dispatch('Sites/deleteSite', props.siteId)
		trackSites(SITES_EVENTS.SITE_DELETED, deleted || props.siteId)
		notify({ group: 'success', text: `Site "${name || ''}" deleted.` })
		router.push({ name: 'SitesDashboard' })
	} catch (err) {
		notify({ group: 'error', text: err.message || 'Unable to delete the site.' })
	}
}
</script>

<template>
	<div class="grid grid-cols-1 gap-4 lg:grid-cols-[minmax(0,3fr)_minmax(0,2fr)] items-start">
		<div class="flex flex-col gap-4">
			<!-- Name -->
			<section class="rounded-lg bg-surface-2 p-4 sm:p-5">
				<h2 class="mb-1 text-headline font-semibold text-surface-text-2">General</h2>
				<p class="mb-4 text-tiny text-surface-7">The name viewers see in the browser tab and site header. Saved changes go live without publishing, within a minute.</p>
				<form class="flex flex-col gap-3 sm:flex-row sm:items-end" @submit.prevent="saveGeneral">
					<FormInput id="site-manage-name" v-model="nameInput" label="Site name" :maxlength="80" class="sm:flex-1" />
					<FormButton size="md" class="shrink-0 justify-center" :disabled="!nameDirty || !nameInput.trim() || savingGeneral"
						:is-loading="savingGeneral" @click="saveGeneral">
						Save
					</FormButton>
				</form>
				<p v-if="checklistDismissed" class="mb-0 mt-4 text-tiny text-surface-7" data-testid="setup-guide-dismissed">
					The setup guide is hidden on the Overview tab.
					<button type="button" class="text-main-light underline-offset-2 hover:underline disabled:opacity-50" :disabled="restoringChecklist" data-testid="show-setup-guide" @click="showSetupGuide">Show setup guide</button>
				</p>
			</section>

			<!-- Address -->
			<section class="rounded-lg bg-surface-2 p-4 sm:p-5">
				<h2 class="mb-1 text-headline font-semibold text-surface-text-2">Address</h2>
				<p class="mb-4 text-tiny text-surface-7">Your site's Castr address. Connect your own under <router-link :to="{ name: 'SiteDomains', params: { siteId } }" class="underline">Domains</router-link>.<SitesPlanLockBadge v-if="customDomainLocked" class="ml-1.5" /></p>
				<div class="flex items-center gap-2 rounded-lg bg-surface-3 px-3 py-2.5">
					<icon-base class="shrink-0 text-surface-8" :width="14" :height="14">
						<icon-globe />
					</icon-base>
					<span class="min-w-0 flex-1 truncate text-sm text-white" :title="site.url">{{ site.url }}</span>
					<StatusPill v-if="addressPending" pill size="sm" :variant="readiness.failed.value ? 'warning' : 'accent'" :pulse="!readiness.failed.value" data-testid="general-address-status">
						{{ readiness.failed.value ? 'Not ready' : 'Setting up' }}
					</StatusPill>
					<span v-if="addressPending" :title="readiness.lockedTitle" data-testid="general-address-copy">
						<ButtonCopyToClipboard :text="site.url" size="sm" fixed-width="w-auto" disabled />
					</span>
					<ButtonCopyToClipboard v-else :text="site.url" size="sm" fixed-width="w-auto" data-testid="general-address-copy" />
				</div>
				<p v-if="addressPending" class="mb-0 mt-2 text-tiny text-surface-8" data-testid="general-address-pending">
					{{ readiness.failed.value ? (readiness.detail.value || 'The address could not be set up.') : 'Your address is still being set up (usually 1–2 minutes). Share it once it is ready.' }}
				</p>
			</section>

			<!-- Home-page template -->
			<section class="rounded-lg bg-surface-2 p-4 sm:p-5" data-testid="general-template" :data-template="template">
				<h2 class="mb-1 text-headline font-semibold text-surface-text-2">Template</h2>
				<p class="mb-4 text-tiny text-surface-7">How the home page is laid out. Switching keeps your library, plans, promo codes, viewers and domains; it goes live without publishing, within a minute.</p>
				<div class="grid grid-cols-1 gap-2 sm:grid-cols-2">
					<div v-for="t in SITE_TEMPLATES" :key="t.id" class="flex flex-col gap-2 rounded-lg border p-2.5" :class="t.id === template ? 'border-main bg-surface-3' : 'border-surface-4'" :data-testid="`general-template-${t.id}`" :data-active="t.id === template">
						<SiteTemplateThumb :template="t.id" :active="t.id === template" />
						<div class="flex items-start justify-between gap-2">
							<div class="min-w-0">
								<p class="mb-0.5 text-sm font-medium text-white">{{ t.title }}</p>
								<p class="mb-0 text-tiny text-surface-7">{{ t.description }}</p>
							</div>
							<StatusPill v-if="t.id === template" pill size="sm" variant="accent" class="shrink-0">Current</StatusPill>
						</div>
					</div>
				</div>
				<div class="mt-4 flex flex-wrap items-center gap-3">
					<FormButton type="secondary" size="md" :disabled="switchingTemplate" :is-loading="switchingTemplate" data-testid="general-template-switch" @click="requestSwitch">
						Switch to {{ otherTemplate.name }}
					</FormButton>
					<router-link v-if="template === TEMPLATE_BROADCAST" :to="{ name: 'SiteBroadcast', params: { siteId } }" class="text-tiny text-main-light underline-offset-2 hover:underline" data-testid="general-template-broadcast-link">Broadcast settings</router-link>
				</div>
				<p v-if="site.templateChangedAt" class="mb-0 mt-3 text-tiny text-surface-7" data-testid="general-template-changed">Last switched {{ new Date(site.templateChangedAt).toLocaleString() }}.</p>
			</section>

			<!-- Social share image (runtime `websiteImage`) -->
			<section class="rounded-lg bg-surface-2 p-4 sm:p-5" data-testid="general-share-image">
				<h2 class="mb-1 text-headline font-semibold text-surface-text-2">Social share image</h2>
				<p class="mb-4 text-tiny text-surface-7">Shown when your site is shared on Facebook, WhatsApp, X… 1200 × 630 px. Leave empty to use an automatic image with your logo and colours.</p>
				<Alert v-if="shareError" type="error" class="!mb-0">{{ shareError }}</Alert>
				<p v-else-if="shareLoaded && !shareWritable" class="mb-0 text-tiny text-surface-7" data-testid="general-share-unsupported">This site's server does not accept a share image yet — your site uses the automatic image.</p>
				<template v-else-if="shareLoaded">
					<SiteImageField id="general-share-image" v-model="shareImage" :site-id="siteId" label="Share image" hint="1200 × 630 px · PNG or JPEG" :recommended-max-bytes="1024 * 1024" />
					<div v-if="!shareImage.trim()" class="mt-4" data-testid="general-share-auto">
						<p class="mb-2 text-tiny font-medium text-surface-text-1">Automatic image</p>
						<div class="aspect-[1200/630] w-full max-w-sm overflow-hidden rounded-md bg-surface-3 ring-1 ring-surface-4">
							<img v-if="autoPreviewSrc && !autoPreviewBroken" :src="autoPreviewSrc" alt="Automatic share image of your home page" class="size-full object-cover" loading="lazy" data-testid="general-share-auto-img" @error="autoPreviewBroken = true">
							<div v-else class="grid size-full place-items-center px-4 text-center text-tiny text-surface-7" data-testid="general-share-auto-pending">
								{{ addressPending ? 'Preview once your address is ready.' : 'Preview unavailable right now — the image is still generated when your site is shared.' }}
							</div>
						</div>
						<p class="mb-0 mt-2 text-tiny text-surface-7">Shows your published site. Video and live event pages get their own card with the title, date or thumbnail.</p>
					</div>
					<div class="mt-4 flex flex-wrap items-center gap-3">
						<FormButton size="md" :disabled="!shareDirty || savingShare" :is-loading="savingShare" data-testid="general-share-save" @click="saveShareImage">{{ shareSaveLabel }}</FormButton>
						<p class="mb-0 min-w-0 flex-1 basis-56 text-tiny text-surface-7">Facebook, X and WhatsApp keep old previews for a while; after publishing, refresh a link in Facebook's <a href="https://developers.facebook.com/tools/debug/" target="_blank" rel="noopener noreferrer" class="underline">Sharing Debugger</a>.</p>
					</div>
				</template>
			</section>
		</div>

		<div class="flex flex-col gap-4">
			<!-- Stripe / monetization -->
			<section class="rounded-lg bg-surface-2 p-4 sm:p-5">
				<h2 class="mb-1 text-headline font-semibold text-surface-text-2">Stripe</h2>
				<p class="mb-4 text-tiny text-surface-7">Only needed to sell plans. Free and registration-only content works without it.</p>
				<Alert v-if="monetizationError" type="error" class="!mb-0">{{ monetizationError }}</Alert>
				<SiteStripeStatus v-else :stripe="monetization?.stripe || null" :monetization="monetization?.monetization || null"
					:unlinking="stripeConnect.unlinking.value" @connected="loadMonetization" @unlink="requestUnlink" />
				<p v-if="monetization?.monetization?.status === 'paused' && monetization?.canMonetize" class="mb-0 mt-3 text-tiny text-surface-7">
					Resume the paused plans under <router-link :to="{ name: 'SitePlans', params: { siteId } }" class="underline">Plans</router-link>.
				</p>
			</section>

			<!-- Danger zone -->
			<section class="rounded-lg bg-surface-2 p-4 sm:p-5 ring-1 ring-error/30">
				<h2 class="mb-1 text-headline font-semibold text-surface-text-2">Delete site</h2>
				<p class="mb-4 text-tiny text-surface-7">The site goes offline immediately and its address is released. This cannot be undone.</p>
				<FormButton type="danger" size="md" @click="onDeleteRequest">Delete site</FormButton>
			</section>
		</div>

		<ConfirmModal modal-id="delete-site-settings" modal-type="danger" ok-text="Delete site" cancel-text="Cancel"
			title="Delete this site?"
			:message="`&quot;${site?.name || 'This site'}&quot; goes offline immediately and its address is released. This cannot be undone.`"
			@modal-confirm="onDelete" />
		<ConfirmModal modal-id="unlink-stripe-general" modal-type="danger" ok-text="Unlink Stripe" cancel-text="Cancel"
			title="Unlink your Stripe account?"
			message="Paid plans on all your sites are paused (viewers keep what they already bought) and paid content stays locked until you reconnect. Your Stripe account and its history stay in your Stripe dashboard."
			@modal-confirm="confirmUnlink" />
		<ActionModal v-model:is-visible="switchModalVisible" :title="switchCopy.title" :body="switchCopy.body" :confirm-text="switchCopy.confirm" cancel-text="Keep current template" :on-submit="confirmSwitch" />
	</div>
</template>
